import type { DropshippingAttachmentDto } from "../../../../api";
import { useAuthenticatedBlob } from "../../../../hooks/useAuthenticatedBlob";
import { Text } from "../../../../components/common/typography";
import { SectionCard } from "../../../../components/common/section-card";
import { Shimmer } from "../../../../components/common/shimmer";

interface Props {
  attachments: DropshippingAttachmentDto[];
  deliveredAt: string | null | undefined;
}

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatDelivered(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FORMATTER.format(d);
}

function isImage(mimetype: string): boolean {
  return mimetype.startsWith("image/");
}

function ProofImage({ attachment }: { attachment: DropshippingAttachmentDto }) {
  const { src, error } = useAuthenticatedBlob(attachment.url);

  if (error) {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl bg-bgMuted">
        <Text variant="bodyXs" tone="muted">Couldn't load {attachment.name}</Text>
      </div>
    );
  }

  if (!src) {
    return <Shimmer className="h-32 w-full rounded-xl" />;
  }

  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="block h-32 overflow-hidden rounded-xl bg-bgMuted"
      title={attachment.name}
    >
      <img src={src} alt={attachment.name} className="h-full w-full object-cover" />
    </a>
  );
}

function ProofFile({ attachment }: { attachment: DropshippingAttachmentDto }) {
  const { src, error } = useAuthenticatedBlob(attachment.url);

  return (
    <div className="flex h-32 flex-col items-center justify-center gap-1 rounded-xl bg-bgMuted px-3 text-center">
      <Text variant="bodySmBold" tone="dark" className="line-clamp-2">{attachment.name}</Text>
      {error ? (
        <Text variant="bodyXs" tone="muted">Couldn't load file</Text>
      ) : src ? (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-bodyXs underline text-textBody"
        >
          Open
        </a>
      ) : (
        <Text variant="bodyXs" tone="muted">Loading…</Text>
      )}
    </div>
  );
}

export function SellerOrderDeliveryProof({ attachments, deliveredAt }: Props) {
  const hasAny = attachments.length > 0;

  return (
    <SectionCard shadow title="Delivery Proof Submitted" className="gap-4">
      <div className="flex items-center justify-between py-2">
        <Text variant="bodySm" tone="muted">
          Submitted: {formatDelivered(deliveredAt)}
        </Text>
        <Text variant="bodySm" tone="muted">
          {hasAny
            ? `${attachments.length} file${attachments.length > 1 ? "s" : ""} attached`
            : "No proof attached"}
        </Text>
      </div>
      {hasAny && (
        <div className="mt-2 grid grid-cols-3 gap-3">
          {attachments.map((att) =>
            isImage(att.mimetype) ? (
              <ProofImage key={att.id} attachment={att} />
            ) : (
              <ProofFile key={att.id} attachment={att} />
            ),
          )}
        </div>
      )}
    </SectionCard>
  );
}
